import React, { createContext, useEffect, useState } from 'react'
import useFetch from "../../utils/useFetch";
import {
    getAuth,
    updatePassword as updatePasswordFirebase,
    updateProfile,
    updateEmail,
    reauthenticateWithCredential,
    EmailAuthProvider,
    signInWithEmailAndPassword
} from 'firebase/auth';
import firebaseApp from "../../utils/firebaseClientApp";

function readCookie(name) {

    const cookieArray = document.cookie.split('; ');
    const cookieObject = cookieArray.reduce((acc, cookie) => {
        const [key, value] = cookie.split('=');
        acc[key] = value;
        return acc;
    }, {});

    return cookieObject[name];
}

function setCookie(name, value, maxAge, session = false) {
    const cookieParts = [
        `${name}=${value}`,
        "path=/"
    ]
    if (session) {
        cookieParts.push('session=')
    }
    if (maxAge) {
        cookieParts.push(`max-age=${maxAge}`)
    }
    const cookie = cookieParts.join('; ')
    document.cookie = cookie
}

function expireCookie(cookieName) {
    setCookie(cookieName, "", 0)
}

const ORGANIZATION_COOKIE_KEY = "organizationId"
const AUTH_TOKEN_COOKIE_NAME = "authToken"

export const CurrentUserContext = createContext({})
export const CurrentUserConsumer = CurrentUserContext.Consumer

const useUser = () => {
    const [user, setUser] = useState()
    useEffect(() => {
        const auth = getAuth(firebaseApp)
        const unregisterAuthObserver = auth.onAuthStateChanged(user => {
            setUser(user);
        });
        return () => unregisterAuthObserver(); // Make sure we un-register Firebase observers when the component unmounts.
    }, []);
    return user;
} 

async function setOrExpireUserSessionCookieForLinks(user) {
    const sessionCookie = true
    if (user) {
        const authToken = await user.getIdToken()
        setCookie(AUTH_TOKEN_COOKIE_NAME, authToken, null, sessionCookie)
    } else {
        expireCookie(AUTH_TOKEN_COOKIE_NAME)
    }
}

export const CurrentUserProvider = ({ children }) => {

    const user = useUser();
    const [currentUserData, setCurrentUserData] = useState();
    const { get } = useFetch();
    const [organizationId, setOrganizationId] = useState()

    useEffect(() => {
        if (user) {
            get(`/api/currentUserData`).then((userData) => {
                setCurrentUserData(userData, false);
            });
        }
        setOrExpireUserSessionCookieForLinks(user).then(x => {
            console.log('cookie set or expired')
        })
    }, [user, organizationId]);



    const setOrganization = (organizationId, reloadPage = true) => {
        organizationId = organizationId || ''
        setOrganizationId(organizationId)
        setCookie(ORGANIZATION_COOKIE_KEY, organizationId, 31536000)
        if (reloadPage) {
            try {
                window.location.reload()
            } catch (e) {
                console.error('Error reloading page:', e)
            }
        }
    }


    useEffect(() => {
        // if no org selected, pick the first one...
        const orgIdCookie = readCookie(ORGANIZATION_COOKIE_KEY)
        if (!orgIdCookie) {
            const firstOrg = userOrganizations[0]?.organizationId
            if (firstOrg)
                setOrganization(firstOrg, false)
        } else {
            // refresh the cookie and set the state up
            setOrganization(orgIdCookie, false)
        }
    }, [currentUserData])

    const permissions = currentUserData?.permissions ?? {}
    const userOrganizations = currentUserData?.organizations ?? []
    const currentOrganization = userOrganizations.find(org => org.organizationId === organizationId) ?? {}

    const getFirebaseAuth = () => getAuth(firebaseApp)

    const signOut = async () => {
        await getFirebaseAuth().signOut()
        setCurrentUserData(undefined)
    }

    const updatePassword = async (oldPassword, newPassword) => {
        const credential = EmailAuthProvider.credential(user.email, oldPassword)
        const reauth = await reauthenticateWithCredential(user, credential)
        console.log({ reauth })
        if (reauth.user) {
            await updatePasswordFirebase(user, newPassword)
        }
    }

    const updateDisplayNameFirebase = async (newName) => {
        const auth = getAuth();
        const updatedProfile = await updateProfile(auth.currentUser, {
            displayName: newName
        });
    };

    const updateEmailFirebase = async (newEmail, credential) => {
        const auth = getAuth();
        const user = auth.currentUser;

        const reauth = await reauthenticateWithCredential(user, credential);
        console.log({ reauth })
        if (reauth.user) {
            await updateEmail(auth.currentUser, newEmail);
        }
    };

    console.log({ user })

    return (
        <CurrentUserContext.Provider value={{
            permissions,
            userOrganizations,
            setOrganization,
            currentOrganization,
            signOut,
            isSignedIn: user || currentUserData,
            updatePassword,
            email: user?.email,
            displayName: user?.displayName,
            updateDisplayNameFirebase,
            updateEmailFirebase
        }}>
            {children}
        </CurrentUserContext.Provider>
    )
}
